import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';
import {ReactNode} from 'react-markdown';

import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import {RED} from 'ui-library/Colors';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, ElementMouseEvent, InputChangeEvent, InputFocusEvent} from 'util/Types';

export interface DoubleClickEditableTextFieldProps {
  value: string;
  viewer: ReactNode;
  onCancel?: () => void;
  onComplete: (changedValue: string) => void;
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  onDraftNameChanged: (name: string) => void;
  isError?: boolean;
  placeholderText?: string;
  maxValueLength?: number;
  emptyStringCompletionPolicy:
    | EmptyStringCompletionPolicy.ALLOW_COMPLETE
    | EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE;
  defaultValue: () => string;
  /** When true, a single click triggers edit mode instead of a double click. Defaults to false. */
  singleClickToEdit?: boolean;
  /** Optional class name applied to the viewer wrapper div. */
  viewerWrapperClassName?: string;
}

export enum EmptyStringCompletionPolicy {
  REPLACE_WITH_DEFAULT_ON_COMPLETE,
  ALLOW_COMPLETE,
}

/**
 * Provides a form field with cancel and complete buttons to allow editing of the desired "viewer"
 * on double click. Based off of {@link EditableTextField}, {@link BaseEditableField},
 * and {@link FieldActionButtons}
 *
 * Note, blurring the editor view will trigger a "complete" action - only "X" button and "esc" keys
 * will trigger "cancel" action.
 */
export default function DoubleClickEditableTextField(props: DoubleClickEditableTextFieldProps): JSX.Element {
  const [draftValue, setDraftValue] = useState(props.value);
  const {isEditing, setIsEditing} = props;

  function handleStartEdit(e: ElementMouseEvent): void {
    e.stopPropagation();
    setDraftValue(props.value);
    setIsEditing(true);
  }

  function handleCancel(): void {
    setDraftValue(props.value);
    props.onCancel?.();
    setIsEditing(false);
  }

  function handleComplete(): void {
    let newValue = draftValue;
    if (
      props.emptyStringCompletionPolicy === EmptyStringCompletionPolicy.REPLACE_WITH_DEFAULT_ON_COMPLETE &&
      newValue.trim() === ''
    ) {
      newValue = props.defaultValue();
      setDraftValue(newValue);
    }
    props.onComplete(newValue);
    setTimeout(() => {
      // Defer telling the caller to close the editor. This gives them one render-cycle to update their
      // internal state
      setIsEditing(false);
    }, 0);
  }

  function handleKeyDown(e: ElementKeyboardEvent): void {
    if (keyboard.isEnterKey(e)) {
      handleComplete();
      e.stopPropagation();
    } else if (keyboard.isEscapeKey(e)) {
      handleCancel();
      e.stopPropagation();
    } else if (keyboard.isADeleteKey(e)) {
      e.stopPropagation();
    }
  }

  function handleBlur(e: InputFocusEvent): void {
    e.stopPropagation();
    e.preventDefault();
    // Focusing on any non-child element should trigger completion
    if (e.relatedTarget == null || !e.currentTarget.contains(e.relatedTarget)) {
      handleComplete();
    }
  }

  const wrappedViewer = (
    <div
      className={classNames(css(styles.wrapper), props.viewerWrapperClassName)}
      onClick={props.singleClickToEdit ? handleStartEdit : undefined}
      onDoubleClick={props.singleClickToEdit ? undefined : handleStartEdit}
      {...getTestProps({component: 'DoubleClickEditableTextField', element: 'root'})}
    >
      {props.viewer}
    </div>
  );

  const editor = (
    <div
      className={css(styles.editor)}
      onBlur={handleBlur}
      {...getTestProps({component: 'DoubleClickEditableTextField', element: 'root'})}
    >
      <FormInput
        className={classNames('u-flex-grow', css(props.isError && styles.editorError))}
        onChange={(event: InputChangeEvent) => {
          setDraftValue(event.target.value);
          props.onDraftNameChanged(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        focusOnMount={true}
        type="text"
        value={draftValue}
        maxLength={props.maxValueLength}
        placeholder={props.placeholderText}
      />
    </div>
  );

  return isEditing ? editor : wrappedViewer;
}

const styles = StyleSheet.create({
  singleButton: {width: '12px', height: '12px'},
  cancelButtonLink: {marginLeft: '8px', marginRight: '4px', padding: '0px'},
  completeButtonLink: {marginLeft: '4px', padding: '0px'},
  wrapper: {width: '100%', overflow: 'hidden'},
  editor: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  },
  editorError: {
    border: `1px solid ${RED}`,
  },
});
